'use strict'
import { useEffect, useState } from 'react'
import axios from 'axios'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Switch,
  FormControlLabel
} from '@material-ui/core'
import { deriveLandingDescriptors, formatAnchorLabel, formatShortDate } from './cadenceAnchorOptions'
import { getStopFrequencyPill } from './refineFrequencyPill'

// Landing dialog for a long-cycle (M/Q/SA/A) concrete visit dropped on a day
// bucket in the refine planner. Dumb by design: classification/label logic
// lives in cadenceAnchorOptions.js, ranked options + the apply live on the
// server (longCycle.controller.js). The parent owns the optimistic board
// state — this dialog only reports intent via the three callbacks:
//   onMoveThisOnly(movePayload)  — user chose the plain scenario move
//   onAnchorApplied(result)      — a cadence anchor was POSTed successfully
//   onClose()                    — cancel/escape, no writes happened
//
// landing = { ymd, routeId (numeric Aspire RouteID or null), routeName,
//             dayOfWeek, weekType, movePayload }

const roundHours = (manMin) => Math.round(Number(manMin || 0) / 60)

function LoadPill ({ preview }) {
  const over = Number(preview.loadManMin) > Number(preview.ceilingManMin)
  return (
    <span
      className="rw-pill"
      title={`${formatShortDate(preview.date)} route load vs capacity`}
      style={{
        background: over ? 'var(--df-warn-bg, #fbe8d6)' : 'var(--df-surface-2, #f4efe4)',
        color: over ? 'var(--df-warn-text, #a4502f)' : 'var(--df-ink-3, #5c6657)',
        fontSize: 11,
        fontWeight: 600
      }}
    >
      {roundHours(preview.loadManMin)}h/{roundHours(preview.ceilingManMin)}h
    </span>
  )
}

export default function LongCycleLandingDialog ({
  open,
  onClose,
  stop,
  landing,
  url,
  weekStartDates,
  onMoveThisOnly,
  onAnchorApplied,
  onSnackbar
}) {
  const [expanded, setExpanded] = useState(false)
  const [optionsState, setOptionsState] = useState({ loading: false, options: null })
  const [pinCrew, setPinCrew] = useState(true)
  const [applyingIndex, setApplyingIndex] = useState(null)

  // Each open is a fresh landing — clear the lazily-fetched options and busy
  // flags from the previous one.
  useEffect(() => {
    if (open) {
      setExpanded(false)
      setOptionsState({ loading: false, options: null })
      setPinCrew(true)
      setApplyingIndex(null)
    }
  }, [open])

  if (!stop || !landing) return null

  const busy = applyingIndex != null
  const descriptors = deriveLandingDescriptors(landing.ymd, weekStartDates)
  const freqPill = getStopFrequencyPill(stop)
  // crew_day pinning needs the numeric Aspire RouteID server-side; without one
  // the only valid scope is day_only.
  const canPinCrew = landing.routeId != null

  const handleExpand = async () => {
    setExpanded(true)
    if (optionsState.options || optionsState.loading) return
    setOptionsState({ loading: true, options: null })
    try {
      const params = { aspirePropertyId: stop.aspirePropertyId, landingDate: landing.ymd }
      if (landing.routeId != null) params.routeId = landing.routeId
      const res = await axios.get(`${url}/api/hmi/territory/long-cycle/anchor-options`, { params })
      setOptionsState({ loading: false, options: res.data?.options || [] })
    } catch (err) {
      // Fetch failure must not strand the dialog — collapse back so
      // "This visit only" stays one click away.
      setOptionsState({ loading: false, options: null })
      setExpanded(false)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to load cadence anchor options', 'error')
    }
  }

  const applyOption = async (option, index) => {
    setApplyingIndex(index)
    try {
      const res = await axios.post(`${url}/api/hmi/territory/long-cycle/cadence-anchor`, {
        aspirePropertyId: stop.aspirePropertyId,
        frequencyCode: stop.frequencyCode,
        anchorType: option.anchorType,
        // Server option params are already in apply shape (companion options
        // carry effectiveType) — pass through untouched.
        params: option.params || {},
        pinScope: pinCrew && canPinCrew ? 'crew_day' : 'day_only',
        routeId: landing.routeId,
        provenance: {
          // Companion options ship provenance (companionPropertyId +
          // companionSnapshot) the server uses for the companion-moved badge.
          ...(option.provenance || {}),
          sourceVisitId: stop.workTicketVisitId || null,
          ...(option.companionPropertyId != null ? { companionPropertyId: option.companionPropertyId } : {})
        }
      })
      const results = res.data?.results || []
      const redated = results.filter(r => !r.skipped && r.to).length
      const skipped = results.filter(r => r.skipped).length
      onSnackbar(
        `Anchored: ${redated} visit${redated === 1 ? '' : 's'} re-dated` +
          (skipped ? ` · ${skipped} skipped (manual overrides)` : ''),
        'success'
      )
      onAnchorApplied(res.data)
    } catch (err) {
      setApplyingIndex(null)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to apply cadence anchor', 'error')
    }
  }

  const anchorLabel = (option) => option.anchorType === 'companion'
    ? formatAnchorLabel({
        anchorType: 'companion',
        params: option.params,
        companionName: option.companionName,
        routeName: landing.routeName
      })
    : formatAnchorLabel(option)

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle style={{ borderBottom: '1px solid var(--df-line, #e0d8c2)', paddingBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--df-ink, #1c2419)' }}>
            {stop.propertyName || `Aspire #${stop.aspirePropertyId}`}
          </span>
          {freqPill && (
            <span className="rw-pill" style={{ background: 'var(--df-surface-2, #f4efe4)', color: 'var(--df-ink-3)', fontSize: 12, fontWeight: 600 }}>
              {freqPill}
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: 'var(--df-ink-4, #97a094)', marginTop: 4 }}>
          Landing {descriptors.weekdayName ? `${descriptors.weekdayName} ` : ''}{formatShortDate(landing.ymd)}
          {landing.routeName ? ` · ${landing.routeName}` : ''}
        </div>
      </DialogTitle>
      <DialogContent style={{ paddingTop: 14 }}>
        <div style={{ fontSize: 13, color: 'var(--df-ink-2, #3a4536)', marginBottom: 12 }}>
          This is a long-cycle visit. Move only this visit, or anchor future visits to this landing?
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            autoFocus
            disabled={busy}
            onClick={() => onMoveThisOnly(landing.movePayload)}
          >
            This visit only
          </Button>
          <Button
            variant="outlined"
            disabled={busy || expanded}
            onClick={handleExpand}
          >
            Future visits too
          </Button>
        </div>

        {expanded && (
          <div style={{ marginTop: 16 }}>
            {optionsState.loading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0' }}>
                <CircularProgress size={18} />
                <span style={{ fontSize: 13, color: 'var(--df-ink-4, #97a094)' }}>Loading anchor options…</span>
              </div>
            )}
            {!optionsState.loading && optionsState.options && (
              <>
                <FormControlLabel
                  control={(
                    <Switch
                      size="small"
                      color="primary"
                      checked={pinCrew && canPinCrew}
                      disabled={busy || !canPinCrew}
                      onChange={(e) => setPinCrew(e.target.checked)}
                    />
                  )}
                  label={(
                    <span style={{ fontSize: 13 }}>
                      Keep on this crew{canPinCrew ? '' : ' (unavailable — no route resolved)'}
                    </span>
                  )}
                  style={{ marginBottom: 8 }}
                />
                {optionsState.options.length === 0 && (
                  <div style={{ fontSize: 13, color: 'var(--df-ink-4, #97a094)', padding: '6px 0' }}>
                    No anchor options available for this landing.
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {optionsState.options.map((option, index) => {
                    const label = anchorLabel(option)
                    if (!label) return null
                    return (
                      <Button
                        key={`${option.anchorType}-${option.companionPropertyId || index}`}
                        variant="outlined"
                        disabled={busy}
                        onClick={() => applyOption(option, index)}
                        style={{ justifyContent: 'flex-start', textTransform: 'none', padding: '8px 12px' }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, width: '100%' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600 }}>
                            {label}
                            {applyingIndex === index && <CircularProgress size={14} />}
                          </span>
                          {(option.nextDates || []).length > 0 && (
                            <span style={{ fontSize: 12, color: 'var(--df-ink-4, #97a094)' }}>
                              {(option.nextDates || []).map(formatShortDate).join(' · ')}
                            </span>
                          )}
                          {(option.loadPreview || []).length > 0 && (
                            <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {option.loadPreview.map(preview => (
                                <LoadPill key={preview.date} preview={preview} />
                              ))}
                            </span>
                          )}
                        </div>
                      </Button>
                    )
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions style={{ borderTop: '1px solid var(--df-line, #e0d8c2)', padding: '10px 24px' }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
      </DialogActions>
    </Dialog>
  )
}
